import React, { useEffect } from "react";
import {
  Play, Pause, SkipBack, SkipForward, Shuffle, Repeat, Repeat1,
  Flame, Mic2, ListMusic, ChevronDown,
} from "lucide-react";
import { motion, useTransform, useMotionValueEvent, animate } from "framer-motion";
import { usePlayer } from "@/contexts/PlayerContext";
import { useAuth } from "@/contexts/AuthContext";
import { formatTime, bigArt } from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import SmartImage from "@/components/SmartImage";

/**
 * NowPlayingView — Apple-Music-style sheet driven by a SHARED motion
 * value (``progress`` 0..1) owned by ``BottomPlayer``.
 *
 * Apple Music feel: the sheet's vertical position is *coupled* to the
 * user's finger as they swipe up from the MiniPlayer (instead of an
 * all-or-nothing trigger). Dragging down on the sheet drives the same
 * shared progress motion value back toward 0. On pointer release the
 * parent decides whether to commit (animate progress → 1) or revert
 * (animate progress → 0) based on position and velocity.
 *
 * This component is ALWAYS mounted (no AnimatePresence). When the
 * progress is 0 it sits offscreen and is ``pointer-events: none`` so
 * it never blocks the rest of the UI.
 */
const SPRING = { type: "spring", stiffness: 300, damping: 32, mass: 0.85 };

const NowPlayingView = ({ progress, onOpen, onClose, onOpenLyrics, onOpenQueue }) => {
  const {
    current, playing, progress: songProgress, duration,
    togglePlay, next, prev, seek,
    shuffle, setShuffle, repeat, setRepeat, toggleLike, isLiked,
  } = usePlayer();
  const { user, login } = useAuth();

  // Sheet y-translate derived from the shared progress motion value.
  // 0% = fully open (top of screen), 100% = fully off-screen below.
  const y = useTransform(progress, [0, 1], ["100%", "0%"]);
  // Backdrop opacity follows progress so the page below dims smoothly
  // during the swipe (matches iOS feel).
  const backdropOpacity = useTransform(progress, [0, 1], [0, 1]);

  // Body scroll lock + pointer-events flip whenever the sheet is even
  // partially visible.
  useEffect(() => {
    let lockedAt = null;
    const unsub = progress.on("change", (v) => {
      const shouldLock = v > 0.05;
      if (shouldLock && lockedAt === null) {
        lockedAt = document.body.style.overflow;
        document.body.style.overflow = "hidden";
      } else if (!shouldLock && lockedAt !== null) {
        document.body.style.overflow = lockedAt;
        lockedAt = null;
      }
    });
    return () => {
      unsub();
      if (lockedAt !== null) document.body.style.overflow = lockedAt;
    };
  }, [progress]);

  // Track "fully closed" state to toggle pointer-events. We use the
  // useMotionValueEvent style via .on but with a state mirror so React
  // can read it for conditional rendering of pointer-events.
  const [isVisible, setIsVisible] = React.useState(false);
  useMotionValueEvent(progress, "change", (v) => {
    setIsVisible(v > 0.02);
  });

  if (!current) return null;

  const liked = isLiked(current.trackId);
  const pct = duration > 0 ? (songProgress / duration) * 100 : 0;

  const handleLike = async () => {
    if (!user) {
      toast.message("Sign in to save songs", { action: { label: "Sign in", onClick: login } });
      return;
    }
    await toggleLike(current);
  };

  const cycleRepeat = () => {
    setRepeat(repeat === "off" ? "all" : repeat === "all" ? "one" : "off");
  };

  // Drag handler — runs while the user drags the sheet (only meaningful
  // when the sheet is open). Each px of vertical drag maps inversely to
  // progress: 60% of viewport height = full close. We clamp to [0, 1]
  // so over-drag at the top is rigid.
  const onDrag = (_, info) => {
    const winH = window.innerHeight || 800;
    const reach = winH * 0.6;
    // info.offset.y is positive when dragging down from the open position
    const next = Math.max(0, Math.min(1, 1 - info.offset.y / reach));
    progress.set(next);
  };

  const onDragEnd = (_, info) => {
    // Commit / revert: position + velocity decision.
    //   • velocity < -400 px/s (fast upward flick) → snap open
    //   • velocity > 400 px/s  (fast downward flick) → snap closed
    //   • else use position threshold at 0.55
    const v = info.velocity.y;
    const p = progress.get();
    if (v < -400) animate(progress, 1, SPRING);
    else if (v > 400) {
      animate(progress, 0, SPRING);
      onClose?.();
    } else if (p < 0.55) {
      animate(progress, 0, SPRING);
      onClose?.();
    } else {
      animate(progress, 1, SPRING);
    }
  };

  return (
    <motion.div
      key="now-playing-sheet"
      data-testid="now-playing-view"
      className="fixed inset-0 z-[70] flex flex-col bg-gradient-to-b from-[#1a1a1a] via-[#0c0c0c] to-black will-change-transform"
      style={{
        y,
        opacity: backdropOpacity,
        pointerEvents: isVisible ? "auto" : "none",
        touchAction: "none",                   // we own the vertical gesture
      }}
      // Coupled vertical drag — Framer Motion drives a delta we map back
      // to ``progress`` so the sheet follows the finger in either direction.
      drag="y"
      dragConstraints={{ top: 0, bottom: 0 }}
      dragElastic={0}                          // we compute the displacement ourselves
      dragMomentum={false}
      dragDirectionLock
      onDrag={onDrag}
      onDragEnd={onDragEnd}
    >
      {/* Hero blurred art background */}
      <div
        className="absolute inset-0 opacity-30 pointer-events-none"
        style={{
          backgroundImage: `url(${bigArt(current.artworkUrl100)})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          filter: "blur(64px)",
          transform: "scale(1.2)",
        }}
      />

      {/* iOS-style drag handle — visual cue that the sheet is swipe-able */}
      <div className="relative w-full flex justify-center pt-2.5 pb-1 shrink-0 select-none">
        <div
          data-testid="now-playing-drag-handle"
          className="w-10 h-1.5 rounded-full bg-white/35"
          aria-hidden="true"
        />
      </div>

      {/* Header */}
      <div className="relative flex items-center justify-between px-4 py-3 shrink-0">
        <button
          data-testid="now-playing-close"
          onClick={() => { animate(progress, 0, SPRING); onClose?.(); }}
          className="p-2 rounded-full hover:bg-white/10 transition"
          type="button"
        >
          <ChevronDown size={28} />
        </button>
        <div className="text-center min-w-0 px-2">
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/45 font-bold">
            Playing from
          </p>
          <p className="text-sm font-semibold truncate max-w-[60vw]">
            {current.collectionName || "Your Queue"}
          </p>
        </div>
        <button
          data-testid="now-playing-queue"
          onClick={onOpenQueue}
          className="p-2 rounded-full hover:bg-white/10 transition"
          type="button"
        >
          <ListMusic size={22} />
        </button>
      </div>

      {/* Body — artwork + meta + transport */}
      <div className="relative flex-1 flex flex-col items-center justify-start px-6 pt-2 pb-4 overflow-hidden">
        {/* Artwork — tap to like (mirrors iOS Music) */}
        <button
          type="button"
          data-testid="now-playing-art"
          onClick={handleLike}
          className="relative w-[min(74vw,420px)] aspect-square rounded-2xl overflow-hidden shadow-[0_24px_80px_-12px_rgba(0,0,0,0.7)] mt-2"
        >
          <SmartImage
            src={bigArt(current.artworkUrl100)}
            alt={current.trackName || ""}
            seed={current.trackName || current.artistName || "?"}
            className="absolute inset-0 w-full h-full"
            imgClassName="w-full h-full object-cover"
            draggable="false"
            tileStyle={{ borderRadius: 0 }}
          />
          {liked && (
            <span className="absolute top-3 right-3 w-9 h-9 rounded-full bg-vermilion text-white flex items-center justify-center shadow-lg">
              <Flame size={18} fill="white" />
            </span>
          )}
        </button>

        {/* Track meta */}
        <div className="mt-6 w-full max-w-[420px] flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h2
              data-testid="now-playing-track"
              className="font-display text-2xl sm:text-3xl font-black tracking-tight leading-tight truncate"
            >
              {current.trackName}
            </h2>
            <Link
              to={current.artistId ? `/artist/${current.artistId}` : "#"}
              data-testid="now-playing-artist"
              className="text-base text-vermilion font-medium truncate inline-block hover:underline"
              onClick={() => { animate(progress, 0, SPRING); onClose?.(); }}
            >
              {current.artistName}
            </Link>
          </div>
          <button
            data-testid="now-playing-fire"
            type="button"
            onClick={handleLike}
            className={`p-2 rounded-full shrink-0 transition ${liked ? "text-vermilion" : "text-white/70 hover:text-white"}`}
            aria-label={liked ? "Unfire" : "Fire"}
          >
            <Flame size={26} fill={liked ? "currentColor" : "transparent"} strokeWidth={2} />
          </button>
        </div>

        {/* Progress bar */}
        <div className="mt-5 w-full max-w-[420px]">
          <input
            data-testid="now-playing-progress"
            type="range"
            min={0}
            max={duration || 0}
            value={songProgress || 0}
            onChange={(e) => seek(parseInt(e.target.value, 10))}
            className="o-slider w-full"
          />
          <div className="mt-1.5 flex justify-between text-[11px] font-medium text-white/50 tabular-nums">
            <span>{formatTime(songProgress)}</span>
            <span>-{formatTime(Math.max(0, (duration || 0) - (songProgress || 0)))}</span>
          </div>
          {/* Progress percentage — primarily for SR users */}
          <div className="sr-only" aria-live="polite">{Math.round(pct)}%</div>
        </div>

        {/* Transport */}
        <div className="mt-7 flex items-center justify-center gap-7 sm:gap-9">
          <button
            data-testid="now-playing-shuffle"
            type="button"
            onClick={() => setShuffle(!shuffle)}
            className={`p-2 transition ${shuffle ? "text-vermilion" : "text-white/65 hover:text-white"}`}
            aria-label="Shuffle"
          >
            <Shuffle size={22} />
          </button>
          <button
            data-testid="now-playing-prev"
            type="button"
            onClick={prev}
            className="p-2 text-white hover:scale-110 active:scale-95 transition"
            aria-label="Previous"
          >
            <SkipBack size={34} fill="white" />
          </button>
          <button
            data-testid="now-playing-play"
            type="button"
            onClick={togglePlay}
            className="w-16 h-16 rounded-full bg-white text-black flex items-center justify-center shadow-[0_10px_36px_-8px_rgba(255,255,255,0.45)] hover:scale-105 active:scale-95 transition"
            aria-label={playing ? "Pause" : "Play"}
          >
            {playing ? <Pause size={28} fill="black" /> : <Play size={28} fill="black" className="ml-1" />}
          </button>
          <button
            data-testid="now-playing-next"
            type="button"
            onClick={next}
            className="p-2 text-white hover:scale-110 active:scale-95 transition"
            aria-label="Next"
          >
            <SkipForward size={34} fill="white" />
          </button>
          <button
            data-testid="now-playing-repeat"
            type="button"
            onClick={cycleRepeat}
            className={`p-2 transition ${repeat !== "off" ? "text-vermilion" : "text-white/65 hover:text-white"}`}
            aria-label="Repeat"
          >
            {repeat === "one" ? <Repeat1 size={22} /> : <Repeat size={22} />}
          </button>
        </div>

        {/* Footer actions — lyrics, queue */}
        <div className="mt-7 flex items-center justify-center gap-3">
          <button
            data-testid="now-playing-lyrics-btn"
            onClick={onOpenLyrics}
            className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/8 hover:bg-white/15 text-sm font-medium transition"
            type="button"
          >
            <Mic2 size={16} /> Lyrics
          </button>
          <button
            data-testid="now-playing-queue-btn"
            onClick={onOpenQueue}
            className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/8 hover:bg-white/15 text-sm font-medium transition"
            type="button"
          >
            <ListMusic size={16} /> Queue
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default NowPlayingView;
